// Campo pontilhado (dither) do palco escuro das telas de auth (WEB-128). Porte do
// `DitherField` do LeztyBot: um fragment shader com matriz de Bayer 4x4 transforma uma
// nuvem de ruído, o pião e a frase da marca em quadradinhos on/off. Mudanças daqui:
// o pião não vem de uma tabela de perfil, vem do vetor da marca (`mascot.tsx`), e é
// pintado em ouro; sem as variantes "tecido" e "gap" que o login não usa.
//
// WebGL e não CSS de propósito: são ~60 mil células recalculadas por quadro, na GPU. A
// animação roda a 30 fps, para sozinha com a aba escondida ou fora da tela e, com
// `prefers-reduced-motion`, congela num quadro fixo (sem mouse, sem giro).
//
// WEB-133: o painel reusa o campo como textura de cartão (`overlay`): sem pião e sem
// frase, só os pontos sobre fundo transparente, com a densidade caindo para a esquerda (o
// texto do cartão fica do lado limpo), e sempre parado (`still`), porque tela de uso
// diário não tem fundo animado.

import { useReducedMotion } from 'motion/react';
import { useEffect, useRef } from 'react';
import { LOWER, UPPER } from './mascot.tsx';

const FRAGMENT = `
precision mediump float;
uniform vec2 uRes; uniform float uTime;
uniform vec3 uDark; uniform vec3 uLight; uniform vec3 uGold; uniform vec3 uInk;
uniform sampler2D uTop; uniform float uHasTop; uniform vec3 uTopPos;
uniform sampler2D uText; uniform float uHasText;
uniform vec2 uMouse; uniform float uHover; uniform float uOverlay;
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y); }
float fbm(vec2 p){ float v = 0.0, a = 0.5; for (int i = 0; i < 5; i++){ v += a * noise(p); p *= 2.02; a *= 0.5; } return v; }
float bayer(vec2 c){
  vec2 m = mod(c, 4.0);
  float x = m.x, y = m.y;
  float b = 0.0;
  if (y < 1.0) b = x < 1.0 ? 0.0 : x < 2.0 ? 8.0 : x < 3.0 ? 2.0 : 10.0;
  else if (y < 2.0) b = x < 1.0 ? 12.0 : x < 2.0 ? 4.0 : x < 3.0 ? 14.0 : 6.0;
  else if (y < 3.0) b = x < 1.0 ? 3.0 : x < 2.0 ? 11.0 : x < 3.0 ? 1.0 : 9.0;
  else b = x < 1.0 ? 15.0 : x < 2.0 ? 7.0 : x < 3.0 ? 13.0 : 5.0;
  return (b + 0.5) / 16.0;
}
void main(){
  vec2 P = floor(gl_FragCoord.xy) + 0.5;
  vec2 uv = P / uRes.y;
  float t = uTime * 0.06;
  vec2 mp = uMouse / uRes.y;
  vec2 dm = uv - mp;
  float md = length(dm);
  float pull = exp(-md * md * 14.0);
  uv += normalize(dm + 1e-5) * uHover * pull * 0.08;
  vec2 q = vec2(fbm(uv * 1.6 + t), fbm(uv * 1.6 - t + 4.7));
  float v = fbm(uv * 1.4 + q * 1.3 + vec2(t * 0.6, -t * 0.4));
  v = smoothstep(0.25, 0.8, v);
  vec2 c = P / uRes - vec2(0.5, 0.62);
  v *= 1.15 - dot(c, c) * 1.6;
  v *= 0.8;
  float halo = exp(-md * md * 40.0);
  float ring = exp(-pow((md - 0.09 - 0.02 * sin(uTime * 3.0)) * 38.0, 2.0));
  v += uHover * (halo * 0.55 + ring * 0.35);
  if (uOverlay > 0.5) {
    vec2 f = P / uRes;
    v = (v + 0.25) * smoothstep(0.5, 1.0, f.x) * smoothstep(0.0, 0.85, f.y);
  }

  float gold = 0.0;
  if (uHasTop > 0.5) {
    vec2 px = P;
    vec2 center = vec2(uTopPos.x * uRes.x, (1.0 - uTopPos.y) * uRes.y);
    float S = uTopPos.z * uRes.y;
    vec2 local = (px - center) / S + 0.5;
    float wob = 0.05 * sin(uTime * 1.4);
    vec2 tip = vec2(0.5, 0.0);
    vec2 d = local - tip;
    local = tip + vec2(d.x * cos(wob) - d.y * sin(wob), d.x * sin(wob) + d.y * cos(wob));
    float top = 0.0;
    if (local.x > 0.0 && local.x < 1.0 && local.y > 0.0 && local.y < 1.0) {
      vec4 tx = texture2D(uTop, vec2(local.x, 1.0 - local.y));
      if (tx.a > 0.5) {
        float nx = tx.r * 2.0 - 1.0;
        float nz = sqrt(max(0.0, 1.0 - nx * nx));
        float lamb = max(0.0, dot(vec2(nx, nz), normalize(vec2(-0.55, 0.85))));
        float spin = pow(max(0.0, cos(nx * 2.6 - uTime * 7.0)), 12.0);
        top = clamp(0.04 + 0.95 * pow(lamb, 2.2) + 0.6 * spin, 0.0, 1.0);
        v = top;
        gold = 1.0;
      }
    }
    vec2 refl = (px - center) / S + 0.5;
    refl.y = -refl.y;
    float mirror = 0.0;
    if (refl.x > 0.0 && refl.x < 1.0 && refl.y > 0.0 && refl.y < 1.0 && top == 0.0) {
      vec4 r = texture2D(uTop, vec2(refl.x, 1.0 - refl.y));
      if (r.a > 0.5) {
        float nx = r.r * 2.0 - 1.0;
        float nz = sqrt(max(0.0, 1.0 - nx * nx));
        float lamb = max(0.0, dot(vec2(nx, nz), normalize(vec2(-0.55, 0.85))));
        float fade = clamp(1.0 - refl.y * 3.2, 0.0, 1.0);
        mirror = clamp(mix(v * 0.5, 0.12 + 0.4 * pow(lamb, 2.0), fade) * 0.45, 0.001, 1.0);
        v = mirror;
        gold = 1.0;
      }
    }
    float near = length((px - center) / S);
    if (top == 0.0 && mirror == 0.0) v *= mix(0.45, 1.0, smoothstep(0.35, 0.9, near));
  }
  float isText = 0.0;
  if (uHasText > 0.5) {
    float yTop = 1.0 - P.y / uRes.y;
    float m = texture2D(uText, vec2(P.x / uRes.x, yTop)).a;
    v *= mix(1.0, 0.3, smoothstep(0.6, 0.95, yTop));
    if (m > 0.5) {
      v = 0.9;
      isText = 1.0;
      gold = 0.0;
    }
  }
  float on = step(bayer(floor(P)), v);
  vec3 lightCol = mix(mix(uLight, uGold, gold), uInk, isText);
  gl_FragColor = uOverlay > 0.5 ? vec4(lightCol * on, on) : vec4(mix(uDark, lightCol, on), 1.0);
}`;

const VERTEX = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }';

type Rgb = [number, number, number];

/** `#rrggbb` para 0..1. Variável ausente ou fora do formato cai no `fallback`. */
function parseHex(raw: string, fallback: Rgb): Rgb {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(raw.trim());
  if (m === null) {
    return fallback;
  }
  const [, r = '00', g = '00', b = '00'] = m;
  return [Number.parseInt(r, 16) / 255, Number.parseInt(g, 16) / 255, Number.parseInt(b, 16) / 255];
}

const TEX = 128;

/**
 * Pião do vetor da marca: silhueta no canal alfa e, no vermelho, a posição horizontal
 * dentro da linha (-1..1), que o shader lê como "normal" de um cilindro para iluminar.
 * A ponta fica embaixo, que é o pivô do bamboleio.
 */
function piaoSource(): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) {
    return null;
  }
  const scale = (TEX - 4) / 111;
  ctx.translate((TEX - 100 * scale) / 2, 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = '#fff';
  ctx.fill(new Path2D(UPPER));
  ctx.fill(new Path2D(LOWER));
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  const image = ctx.getImageData(0, 0, TEX, TEX);
  const px = image.data;
  for (let y = 0; y < TEX; y++) {
    let min = TEX;
    let max = -1;
    for (let x = 0; x < TEX; x++) {
      if ((px[(y * TEX + x) * 4 + 3] ?? 0) > 127) {
        min = Math.min(min, x);
        max = Math.max(max, x);
      }
    }
    const mid = (min + max) / 2;
    const half = (max - min) / 2 + 0.5;
    for (let x = 0; x < TEX; x++) {
      const i = (y * TEX + x) * 4;
      const inside = max >= 0 && (px[i + 3] ?? 0) > 127;
      px[i] = inside ? Math.round(((x - mid) / half / 2 + 0.5) * 255) : 0;
      px[i + 1] = 0;
      px[i + 2] = 0;
      px[i + 3] = inside ? 255 : 0;
    }
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

interface TextSpec {
  lines: string[];
  /** Posição e tamanho como fração da área (x e y) e da altura (size). */
  x: number;
  y: number;
  size: number;
}

interface PiaoSpec {
  x: number;
  y: number;
  size: number;
}

interface Props {
  className?: string;
  /** Lado de cada quadradinho em px de CSS. */
  cell?: number;
  /** Sem pião, o campo é só a nuvem de pontos. */
  piao?: PiaoSpec | undefined;
  /** Sem frase, nada é escrito no campo. */
  text?: TextSpec | undefined;
  /** Congela o campo: um quadro fixo, sem mouse. Sempre ligado com reduced motion. */
  still: boolean;
  /** Textura de cartão: só os pontos, fundo transparente, densidade caindo para a esquerda. */
  overlay: boolean;
}

function textSource(spec: TextSpec, w: number, h: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    return null;
  }
  const size = Math.max(8, Math.round(spec.size * w));
  ctx.fillStyle = '#fff';
  ctx.font = `700 ${size}px Inter, system-ui, sans-serif`;
  ctx.textBaseline = 'alphabetic';
  const lead = 1.02 * size;
  spec.lines.forEach((line, i) => {
    ctx.fillText(
      line,
      Math.round(spec.x * w),
      Math.round(spec.y * h - (spec.lines.length - 1 - i) * lead),
    );
  });
  return canvas;
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (shader === null) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return shader;
}

function setTexture(gl: WebGLRenderingContext, unit: number, tex: WebGLTexture | null): void {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}

/** Liga o shader no canvas. Devolve a função que desliga tudo (ou `undefined` sem WebGL). */
function start(canvas: HTMLCanvasElement, props: Props): (() => void) | undefined {
  const { cell = 3, piao, text, still, overlay } = props;
  const gl = canvas.getContext('webgl', { antialias: false });
  const host = canvas.parentElement;
  if (gl === null || host === null) {
    return undefined;
  }
  const style = getComputedStyle(canvas);
  const dark = parseHex(style.getPropertyValue('--auth-stage'), [0.11, 0.11, 0.14]);
  const light = parseHex(style.getPropertyValue('--auth-dots'), [0.55, 0.54, 0.6]);
  const gold = parseHex(style.getPropertyValue('--auth-gold'), [0.96, 0.86, 0.56]);
  const ink = parseHex(style.getPropertyValue('--auth-ink'), [0.96, 0.96, 0.97]);

  const program = gl.createProgram();
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
  if (vertex === null || fragment === null) {
    return undefined;
  }
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    return undefined;
  }
  // biome-ignore lint/correctness/useHookAtTopLevel: `useProgram` é método do WebGL, não um hook.
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const attr = gl.getAttribLocation(program, 'p');
  gl.enableVertexAttribArray(attr);
  gl.vertexAttribPointer(attr, 2, gl.FLOAT, false, 0, 0);

  const loc = (name: string): WebGLUniformLocation | null => gl.getUniformLocation(program, name);
  gl.uniform3fv(loc('uDark'), dark);
  gl.uniform3fv(loc('uLight'), light);
  gl.uniform3fv(loc('uGold'), gold);
  gl.uniform3fv(loc('uInk'), ink);
  gl.uniform1f(loc('uOverlay'), overlay ? 1 : 0);

  const piaoCanvas = piao === undefined ? null : piaoSource();
  const piaoTex = gl.createTexture();
  gl.uniform1f(loc('uHasTop'), piaoCanvas === null ? 0 : 1);
  if (piaoCanvas !== null && piao !== undefined) {
    setTexture(gl, 0, piaoTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, piaoCanvas);
    gl.uniform1i(loc('uTop'), 0);
    gl.uniform3f(loc('uTopPos'), piao.x, piao.y - 0.5 * piao.size, piao.size);
  }

  const textTex = gl.createTexture();
  gl.uniform1i(loc('uText'), 1);
  const uploadText = (w: number, h: number): void => {
    const source = text === undefined ? null : textSource(text, w, h);
    gl.uniform1f(loc('uHasText'), source === null ? 0 : 1);
    if (source !== null) {
      setTexture(gl, 1, textTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    }
  };

  const uRes = loc('uRes');
  const uTime = loc('uTime');
  const uMouse = loc('uMouse');
  const uHover = loc('uHover');

  const resize = (): void => {
    const w = Math.max(1, Math.ceil(host.clientWidth / cell));
    const h = Math.max(1, Math.ceil(host.clientHeight / cell));
    canvas.style.width = `${w * cell}px`;
    canvas.style.height = `${h * cell}px`;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      uploadText(w, h);
    }
    gl.uniform2f(uRes, w, h);
  };

  // Mouse em células; `target` é onde o ponteiro está, `eased` é quem segue com atraso.
  const target = { x: -999, y: -999, on: 0 };
  const eased = { x: -999, y: -999, on: 0 };
  let frame = 0;
  let visible = true;
  let last = 0;
  const t0 = performance.now();

  const schedule = (): void => {
    if (frame === 0 && visible && !document.hidden) {
      frame = requestAnimationFrame(tick);
    }
  };

  const tick = (now: number): void => {
    frame = 0;
    if (now - last >= 33) {
      last = now;
      eased.x += (target.x - eased.x) * 0.18;
      eased.y += (target.y - eased.y) * 0.18;
      eased.on += (target.on - eased.on) * 0.08;
      gl.uniform2f(uMouse, eased.x, eased.y);
      gl.uniform1f(uHover, eased.on);
      gl.uniform1f(uTime, still ? 8 : (now - t0) / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    const settling =
      !still &&
      (Math.abs(target.on - eased.on) > 0.004 ||
        Math.abs(target.x - eased.x) > 0.3 ||
        Math.abs(target.y - eased.y) > 0.3);
    if (!still || settling) {
      schedule();
    }
  };

  const onMove = (event: PointerEvent): void => {
    if (still || event.pointerType === 'touch') {
      return;
    }
    const r = canvas.getBoundingClientRect();
    const inside =
      event.clientX >= r.left &&
      event.clientX <= r.right &&
      event.clientY >= r.top &&
      event.clientY <= r.bottom;
    if (!inside) {
      target.on = 0;
      return;
    }
    target.x = ((event.clientX - r.left) / r.width) * canvas.width;
    target.y = (1 - (event.clientY - r.top) / r.height) * canvas.height;
    target.on = 1;
    if (eased.x < -900) {
      eased.x = target.x;
      eased.y = target.y;
    }
    schedule();
  };
  const onLeave = (): void => {
    target.on = 0;
  };

  window.addEventListener('pointermove', onMove, { passive: true });
  document.addEventListener('pointerleave', onLeave);
  const resizer = new ResizeObserver(() => {
    resize();
    if (still) {
      last = 0;
      schedule();
    }
  });
  resizer.observe(host);
  resize();

  const watcher = new IntersectionObserver(([entry]) => {
    visible = entry?.isIntersecting ?? true;
    schedule();
  });
  watcher.observe(canvas);
  const onVisibility = (): void => {
    schedule();
  };
  document.addEventListener('visibilitychange', onVisibility);
  // Uma fonte que chega depois do primeiro quadro refaz a textura do texto. `alive` barra
  // a promessa de uma montagem já desfeita (o StrictMode monta duas vezes no MESMO canvas):
  // ela escreveria um uniform do programa antigo com o novo em uso.
  let alive = true;
  if (text !== undefined) {
    void document.fonts?.load('700 16px Inter').then(() => {
      if (!alive) {
        return;
      }
      uploadText(canvas.width, canvas.height);
      last = 0;
      schedule();
    });
  }
  schedule();

  return () => {
    alive = false;
    cancelAnimationFrame(frame);
    watcher.disconnect();
    resizer.disconnect();
    window.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerleave', onLeave);
    document.removeEventListener('visibilitychange', onVisibility);
    // Sem `loseContext()`: o StrictMode remonta o efeito no MESMO canvas e receberia o
    // contexto já perdido. O navegador libera o contexto quando o canvas sai da árvore.
  };
}

export function DitherField({
  className,
  cell = 3,
  piao,
  text,
  still = false,
  overlay = false,
}: Partial<Props>) {
  const ref = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion() ?? false;
  const frozen = still || reduced;
  const lines = text?.lines.join('|');

  // biome-ignore lint/correctness/useExhaustiveDependencies: `text`/`piao` são literais do chamador; as chaves primitivas abaixo é que decidem o refazer.
  useEffect(() => {
    const canvas = ref.current;
    if (canvas === null || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    return start(canvas, { cell, piao, text, still: frozen, overlay });
  }, [cell, frozen, overlay, lines, text?.x, text?.y, text?.size, piao?.x, piao?.y, piao?.size]);

  return (
    // biome-ignore lint/a11y/noAriaHiddenOnFocusable: canvas decorativo, nunca recebe foco.
    <canvas
      ref={ref}
      aria-hidden="true"
      className={className}
      style={{ imageRendering: 'pixelated' }}
    />
  );
}
